import type { ApiResponse } from "@/types/api";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface FetchOptions<T = unknown> {
  method?: HttpMethod;
  body?: T;
  credentials?: RequestCredentials;
  headers?: Record<string, string>;
}

export async function apiFetch<R = unknown, B = unknown>(
  endpoint: string,
  options: FetchOptions<B> = {}
): Promise<ApiResponse<R>> {
  const {
    method = "GET",
    body,
    credentials = "include",
    headers = {},
  } = options;

  const isFormData = body instanceof FormData;

  const response = await fetch(endpoint, {
    method,
    credentials,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...headers,
    },
    ...(body && method !== "GET" ? { body: isFormData ? body : JSON.stringify(body) } : {}),
  });

  let data: ApiResponse<R>;
  try {
    data = await response.json();
  } catch {
    data = {
      success: response.ok,
      message: response.ok ? undefined : `Erro ${response.status}: ${response.statusText}`,
    };
  }

  if (!response.ok) {
    return {
      success: false,
      error: data.error || data.message || `Erro ${response.status}: ${response.statusText}`,
    };
  }

  return data;
}

export function buildApiPath(endpoint: string): string {
  return `/api/${endpoint}`;
}

export const API_ENDPOINTS = {
  contact: {
    proBono: buildApiPath("contact/pro-bono.php"),
    psicologia: buildApiPath("contact/psicologia.php"),
    orderBook: buildApiPath("contact/order-book.php"),
    orderBookDedication: buildApiPath("contact/order-book-dedication.php"),
    orderBookV2: buildApiPath("contact/order-book-v2.php"),
  },
  auth: {
    login: buildApiPath("auth/login.php"),
    logout: buildApiPath("auth/logout.php"),
    me: buildApiPath("auth/me.php"),
    clientLogin: buildApiPath("auth/client-login.php"),
    clientLogout: buildApiPath("auth/client-logout.php"),
    clientMe: buildApiPath("auth/client-me.php"),
  },
  maintenance: {
    status: buildApiPath("maintenance/status.php"),
    toggle: buildApiPath("maintenance/toggle.php"),
  },
  clients: {
    list: buildApiPath("clients/list.php"),
    create: buildApiPath("clients/create.php"),
    toggle: buildApiPath("clients/toggle.php"),
    delete: buildApiPath("clients/delete.php"),
  },
  orders: {
    list: buildApiPath("orders/list.php"),
    updateStatus: buildApiPath("orders/update-status.php"),
  },
} as const;
